import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from 'chart.js';
import { ProductInteractionData } from '@models/statistics';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface ProductInteractionChartProps {
  data: ProductInteractionData[];
}

const ProductInteractionChart: React.FC<ProductInteractionChartProps> = ({ data }) => {
  const chartData = {
    labels: data.map((item) => moment(item.date).format('MM/DD')),
    datasets: [
      {
        label: '찜 비율 (%)',
        data: data.map((item) => item.favoriteRatio),
        borderColor: '#FF6384',
        backgroundColor: '#FFB1C1',
        tension: 0.1,
      },
      {
        label: '하단버튼 클릭 비율 (%)',
        data: data.map((item) => item.purchaseRatio),
        borderColor: '#36A2EB',
        backgroundColor: '#9AD0F5',
        tension: 0.1,
      },
      {
        label: '결제 클릭 비율 (%)',
        data: data.map((item) => item.paymentRatio),
        borderColor: '#FFCD56',
        backgroundColor: '#FFE4A9',
        tension: 0.1,
      },
      {
        label: '결제 비율 (%)',
        data: data.map((item) => item.actualPaymentRatio),
        borderColor: '#4BC0C0',
        backgroundColor: '#A5DFDF',
        tension: 0.1,
      },
      {
        label: '리뷰 클릭 비율 (%)',
        data: data.map((item) => item.reviewRatio),
        borderColor: '#9966FF',
        backgroundColor: '#C9B3FF',
        tension: 0.1,
      },
      {
        label: '공유 클릭 비율 (%)',
        data: data.map((item) => item.shareRatio),
        borderColor: '#FF9933',
        backgroundColor: '#FFD6A9',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: '상품 뷰수 대비 상호작용 비율',
      },
      tooltip: {
        callbacks: {
          afterLabel: (context: TooltipItem<'line'>) => {
            const dataIndex = context.dataIndex;
            const item = data[dataIndex];
            return [
              `하단버튼: ${item?.purchaseRatio} / 결제클릭: ${item?.paymentRatio} / 결제: ${item?.actualPaymentRatio}`,
              `리뷰클릭: ${item?.reviewRatio} / 공유: ${item?.shareRatio} / 찜: ${item?.favoriteRatio}`,
              `상품 뷰수: ${item?.viewCount}회`,
              `찜: ${item?.favoriteCount}회`,
              `하단버튼 클릭: ${item?.purchaseClickCount}회`,
              `결제 클릭: ${item?.paymentClickCount}회`,
              `결제: ${item?.actualPaymentCount}회`,
              `리뷰 클릭: ${item?.reviewClickCount}회`,
              `공유 클릭: ${item?.shareCount}회`,
            ];
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '비율 (%)',
        },
      },
      x: {
        title: {
          display: true,
          text: '날짜',
        },
      },
    },
  };

  return <Line data={chartData} options={options} />;
};

export default ProductInteractionChart;
